import React from 'react';
import { Modal } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faChevronLeft, faPercent, faReceipt, faShekelSign, faXmark } from '@fortawesome/free-solid-svg-icons';
import { resolveTip, roundMoney } from 'shared-models/browser';
import { ValueEntryModal } from '../../../../../components/ValueEntryModal/ValueEntryModal';
import type { ValueEntryResult } from '../../../../../components/ValueEntryModal/ValueEntryModal';
import type { TipInputMode, TipPayload } from '../../../../../services/payments/payments.service';
import { Button } from '../../../../../components/Button';
import classes from './tip-mode-chooser.module.scss';

type TipModalProps = {
  isOpen: boolean;
  title: string;
  /** percent basis: cart/items total for the order tip, the line amount for a line tip */
  base: number;
  paymentBase?: number;
  /** order tip → offer the scope cards (על כל ההזמנה / חד פעמי); line tip → no scope */
  showScope: boolean;
  additive?: boolean;
  currentMode: TipInputMode | null;
  currentValue: number | null;
  isSubmitting: boolean;
  onClose: () => void;
  onConfirm: (payload: TipPayload) => void;
};

const TIP_PCT_PRESETS = [10, 12, 15, 20];
const TIP_AMT_PRESETS = [5, 10, 20, 50];
const TIP_MAX_PERCENT = 200;
const TIP_MAX_VALUE = 9999;

const TIP_ENTRY_OPTIONS = [
  { mode: 'percentage', label: 'טיפ באחוזים', description: 'אחוז מסכום החשבון', icon: faPercent },
  { mode: 'amount', label: 'טיפ בשקלים', description: 'סכום קבוע שמתווסף לחשבון', icon: faShekelSign },
  { mode: 'total', label: 'סכום כולל טיפ', description: 'מזינים את הסה״כ לתשלום, הטיפ מחושב מההפרש', icon: faReceipt },
] as const;

const TIP_SCOPE = {
  allLabel: 'טיפ על כל ההזמנה',
  allSub: 'מתעדכן עם פריטים שיתווספו',
  snapshotLabel: 'טיפ חד פעמי',
  snapshotSub: 'סכום קבוע על המצב הנוכחי',
};

// Tip entry on the shared ValueEntryModal (discount-modal design). Scope decides persistence:
// percent + 'all' → live percent; percent + 'חד פעמי' / amount → frozen ₪ value. See FUNCTIONS.md.
const TipModalComponent: React.FC<TipModalProps> = ({
  isOpen,
  title,
  base,
  paymentBase = base,
  showScope,
  additive = false,
  currentMode,
  currentValue,
  isSubmitting,
  onClose,
  onConfirm,
}) => {
  const [entryMode, setEntryMode] = React.useState<'percentage' | 'amount' | 'total' | null>(null);
  React.useEffect(() => {
    if (!isOpen) setEntryMode(null);
  }, [isOpen]);
  const existingAmount = resolveTip(currentMode ?? null, currentValue ?? null, base);

  const toPayload = (result: ValueEntryResult): TipPayload => {
    if (additive) {
      return { tipInputMode: 'value', tipInputValue: roundMoney(Math.max(0, result.amount)) };
    }
    if (result.amount <= 0) {
      return { tipInputMode: null, tipInputValue: null };
    }
    if (result.mode === 'percentage' && result.scope === 'all') {
      return { tipInputMode: 'percent', tipInputValue: result.rawInput };
    }
    // amount mode, or a one-time ('חד פעמי') percent → freeze to a fixed ₪ value
    return { tipInputMode: 'value', tipInputValue: roundMoney(result.amount) };
  };

  if (!isOpen) return null;
  if (entryMode === null) {
    return (
      <Modal opened={isOpen} onClose={onClose} centered withCloseButton={false} closeOnClickOutside={false}
        size={640} padding={0} classNames={{ content: classes.modal, body: classes.body }} aria-label="הוספת טיפ">
        <div dir="rtl" data-testid="tip-mode-chooser">
          <div className={classes.header}>
            <div><h2>הוספת טיפ</h2><p>איך תרצו לחשב את הטיפ?</p></div>
            <Button variant="icon" ariaLabel="סגור" className={classes.close} onClick={onClose} disabled={isSubmitting}>
              <FontAwesomeIcon icon={faXmark as IconProp} />
            </Button>
          </div>
          <div className={classes.options}>
            {TIP_ENTRY_OPTIONS.map((option) => (
              <Button key={option.mode} variant="cancel" fullWidth className={`${classes.option} btn-bordered`}
                dataTestId={`tip-mode-${option.mode}`} onClick={() => setEntryMode(option.mode)} disabled={isSubmitting}>
                <span className={classes.icon} aria-hidden="true"><FontAwesomeIcon icon={option.icon as IconProp} /></span>
                <span className={classes.copy}><strong>{option.label}</strong><span>{option.description}</span></span>
                <FontAwesomeIcon icon={faChevronLeft as IconProp} className={classes.chevron} aria-hidden="true" />
              </Button>
            ))}
            <Button variant="cancel" fullWidth className={`${classes.cancel} btn-bordered`} onClick={onClose} disabled={isSubmitting}>ביטול</Button>
          </div>
        </div>
      </Modal>
    );
  }

  const totalEntry = entryMode === 'total';
  return (
    <ValueEntryModal
      isOpen={isOpen}
      title={title}
      initialMode={entryMode === 'percentage' ? 'percentage' : 'amount'}
      hideModeToggle
      prefillAmount={totalEntry ? paymentBase : undefined}
      amountOffset={totalEntry ? paymentBase : undefined}
      percentBase={base}
      infoRightLabel={totalEntry ? 'סכום בסיס' : 'סה״כ פריטים'}
      infoRightValue={totalEntry ? paymentBase : base}
      inputLabels={{ percentage: 'אחוז טיפ', amount: totalEntry ? 'סכום כולל טיפ' : 'סכום טיפ' }}
      presets={totalEntry ? undefined : { percentage: TIP_PCT_PRESETS, amount: TIP_AMT_PRESETS }}
      scope={showScope ? TIP_SCOPE : undefined}
      maxPercent={TIP_MAX_PERCENT}
      maxAmount={TIP_MAX_VALUE}
      capPercentMessage="אחוז הטיפ חייב להיות בין 0 ל-200"
      capAmountMessage="הטיפ גבוה מהמותר"
      existingAmount={additive ? 0 : existingAmount}
      dataTestId="tip"
      removeLabel="בטל טיפ"
      isSaving={isSubmitting}
      onClose={onClose}
      onConfirm={(result) => onConfirm(toPayload(result))}
      onRemove={() => onConfirm({ tipInputMode: null, tipInputValue: null })}
    />
  );
};

export const TipModal = React.memo(TipModalComponent);
